import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Paper, Stack, TextField, Autocomplete, Button,
  FormControl, InputLabel, Select, MenuItem, Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import apiInstance from 'src/utils/apiAuth';

// Normalizers → every option becomes {id, label} so one getOptionLabel works everywhere.
const asLabelled = (arr) => (arr || []).map((x) => ({ id: x.id, label: x.label }));
const asNamed = (arr) => (arr || []).map((x) => ({ id: x.id, label: x.name }));
const asPeople = (arr, idKey = 'id') =>
  (arr || []).map((x) => ({
    id: x[idKey],
    label: `${x.first_name || ''} ${x.last_name || ''}`.trim() || `#${x[idKey]}`,
  }));

const DATE_FMT = 'YYYY-MM-DD';

// Global filter bar. Emits ONE filters object to the parent, which fires ONE
// getSummary fetch (SC-003). Options for child dimensions cascade on their parent.
export default function CommandCenterFilters({ filters, onChange, availableCurrencies, selectedCurrencyId }) {
  const [countries, setCountries] = useState([]);
  const [practices, setPractices] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [segments, setSegments] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [partners, setPartners] = useState([]);
  const [teams, setTeams] = useState([]);
  const [owners, setOwners] = useState([]);

  const update = (patch) => onChange({ ...filters, ...patch });

  // Static option lists (independent of any parent selection).
  useEffect(() => {
    const load = async (url, setter, mapper) => {
      try {
        const res = await apiInstance.get(url);
        setter(mapper(res?.data?.data || res?.data || []));
      } catch (e) {
        setter([]);
      }
    };
    load('location/?company_added=true', setCountries, asLabelled);
    load('practice/', setPractices, asLabelled);
    load('business-type/', setBusinessTypes, asLabelled);
    load('partner/', setPartners, (a) => asPeople(a));
    load('teams/', setTeams, asNamed);
    load('users-viewset/?short_details=true&dashboard_view=true', setOwners, (a) => asPeople(a, 'pk'));
  }, []);

  // Offering options cascade on selected practice(s); with none selected, load all.
  useEffect(() => {
    let cancelled = false;
    const loadOfferings = async () => {
      try {
        const pracIds = (filters.practices || []).map((p) => p.id);
        let data = [];
        if (pracIds.length) {
          const results = await Promise.all(
            pracIds.map((pid) => apiInstance.get(`offering/?practice_id=${pid}`).catch(() => null)),
          );
          const merged = {};
          results.forEach((r) => (r?.data?.data || []).forEach((o) => { merged[o.id] = o; }));
          data = Object.values(merged);
        } else {
          const r = await apiInstance.get('offering');
          data = r?.data?.data || [];
        }
        if (!cancelled) setOfferings(asLabelled(data));
      } catch (e) {
        if (!cancelled) setOfferings([]);
      }
    };
    loadOfferings();
    return () => { cancelled = true; };
  }, [filters.practices]);

  // Segment options cascade on selected offering(s).
  useEffect(() => {
    let cancelled = false;
    const loadSegments = async () => {
      const offIds = (filters.offerings || []).map((o) => o.id);
      if (!offIds.length) {
        setSegments([]);
        return;
      }
      try {
        const results = await Promise.all(
          offIds.map((oid) => apiInstance.get(`segment/?offering_id=${oid}`).catch(() => null)),
        );
        const merged = {};
        results.forEach((r) => (r?.data?.data || []).forEach((s) => { merged[s.id] = s; }));
        if (!cancelled) setSegments(asLabelled(Object.values(merged)));
      } catch (e) {
        if (!cancelled) setSegments([]);
      }
    };
    loadSegments();
    return () => { cancelled = true; };
  }, [filters.offerings]);

  const multi = (label, options, value, key, extraPatch = {}) => (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={value || []}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(o) => o.label || ''}
      onChange={(e, val) => update({ [key]: val, ...extraPatch })}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={label} InputLabelProps={{ shrink: true }} />
      )}
      sx={{ minWidth: 200, flex: '1 1 200px' }}
    />
  );

  const clearAll = () =>
    onChange({
      startDate: null, endDate: null, currencyId: null,
      countries: [], practices: [], offerings: [], segments: [],
      businessTypes: [], teams: [], owners: [], partners: [],
      trendMonths: 12,
    });

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start date"
            value={filters.startDate ? dayjs(filters.startDate) : null}
            onChange={(v) => update({ startDate: v ? v.format(DATE_FMT) : null })}
            format="DD-MM-YYYY"
            slotProps={{ textField: { size: 'small', InputLabelProps: { shrink: true }, sx: { minWidth: 170 } } }}
          />
          <DatePicker
            label="End date"
            value={filters.endDate ? dayjs(filters.endDate) : null}
            onChange={(v) => update({ endDate: v ? v.format(DATE_FMT) : null })}
            format="DD-MM-YYYY"
            slotProps={{ textField: { size: 'small', InputLabelProps: { shrink: true }, sx: { minWidth: 170 } } }}
          />
        </LocalizationProvider>

        {multi('Country', countries, filters.countries, 'countries')}
        {multi('Practice', practices, filters.practices, 'practices', { offerings: [], segments: [] })}
        {multi('Offering', offerings, filters.offerings, 'offerings', { segments: [] })}
        {multi('Segment', segments, filters.segments, 'segments')}
        {multi('Business Type', businessTypes, filters.businessTypes, 'businessTypes')}
        {multi('Team', teams, filters.teams, 'teams')}
        {multi('Owner', owners, filters.owners, 'owners')}
        {multi('Partner', partners, filters.partners, 'partners')}

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel shrink id="cc-currency-label">Currency</InputLabel>
          <Select
            labelId="cc-currency-label"
            label="Currency"
            displayEmpty
            value={selectedCurrencyId ?? ''}
            onChange={(e) => update({ currencyId: e.target.value === '' ? null : e.target.value })}
          >
            {(availableCurrencies || []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.label}{typeof c.deal_count === 'number' ? ` (${c.deal_count})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Trend months"
          type="number"
          size="small"
          value={filters.trendMonths ?? 12}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            update({ trendMonths: Number.isNaN(n) ? 12 : Math.min(36, Math.max(1, n)) });
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 130 }}
        />

        <Button variant="outlined" color="secondary" size="small" onClick={clearAll} sx={{ height: 40 }}>
          Clear
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Money figures are scoped to the selected currency (no cross-currency conversion). Counts span all currencies.
      </Typography>
    </Paper>
  );
}
